import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchVmDetail } from '../services/esxiApi';

/**
 * Carica su richiesta il dettaglio di una VM (`/vms/:catalogId`) per arricchire
 * i campi non presenti nell'elenco (es. sistema operativo, datastore). Il
 * risultato viene fuso sopra l'item di elenco. Idempotente: carica una sola
 * volta per catalogId, annulla la richiesta allo smontaggio.
 */
export function useVmDetail(catalogId, { enabled }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const load = useCallback(() => {
    if (!catalogId || loadedRef.current) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    fetchVmDetail(catalogId, controller.signal)
      .then((data) => {
        if (!mountedRef.current) return;
        setDetail(data);
        loadedRef.current = true;
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        if (!mountedRef.current) return;
        setError('Impossibile caricare il dettaglio della VM.');
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [catalogId]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { detail, loading, error };
}
